import { createContext, useContext, useEffect, useState } from "react";

import {
  getTotalPrice,
  getFinalPrice,
  getDiscountAmount,
} from "../utils/cartHelpers";

import { validatePurchase } from "../services/purchaseService";

import toast from "react-hot-toast";

const CartContext = createContext();

export function CartProvider({ children }) {
  /*
    |--------------------------------------------------------------------------
    | CART
    |--------------------------------------------------------------------------
    */

  const [cart, setCart] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("cart")) || [];

    return saved.filter((item) => !item.selectedVariation?.isExpired);
  });

  /*
    |--------------------------------------------------------------------------
    | NEXT CART
    |--------------------------------------------------------------------------
    */

  const [nextCart, setNextCart] = useState(() => {
    return JSON.parse(localStorage.getItem("nextCart")) || [];
  });

  /*
    |--------------------------------------------------------------------------
    | VALIDATION STATES
    |--------------------------------------------------------------------------
    */

  const [validatingItem, setValidatingItem] = useState(null);

  const [movingToCartKey, setMovingToCartKey] = useState(null);

  const [invalidCartItems, setInvalidCartItems] = useState({});

  /*
    |--------------------------------------------------------------------------
    | SAVE CART
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  /*
    |--------------------------------------------------------------------------
    | SAVE NEXT CART
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    localStorage.setItem("nextCart", JSON.stringify(nextCart));
  }, [nextCart]);

  /*
    |--------------------------------------------------------------------------
    | STORAGE EVENT
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "cart") {
        setCart(JSON.parse(event.newValue || "[]"));
      }

      if (event.key === "nextCart") {
        setNextCart(JSON.parse(event.newValue || "[]"));
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  /*
    |--------------------------------------------------------------------------
    | UNIQUE ITEM KEY
    |--------------------------------------------------------------------------
    */

  const getCartItemKey = (productId, variationId = null, giftId = null) => {
    return [productId, variationId ?? "no-variation", giftId ?? "no-gift"].join(
      "-",
    );
  };

  /*
    |--------------------------------------------------------------------------
    | ADD TO CART
    |--------------------------------------------------------------------------
    */

  const addToCart = (product) => {
    if (product.selectedVariation?.isExpired) {
      toast.error("این محصول منقضی شده و قابل خرید نیست.");

      return false;
    }

    setCart((previousCart) => {
      const variationId = product.selectedVariation?.variationId ?? null;

      const giftId = product.selectedGift?.id ?? null;

      const itemKey = getCartItemKey(product.id, variationId, giftId);

      const existingIndex = previousCart.findIndex(
        (item) =>
          getCartItemKey(
            item.id,
            item.selectedVariation?.variationId ?? null,
            item.selectedGift?.id ?? null,
          ) === itemKey,
      );

      const quantity = Number(product.qty) || 1;

      const stock = Number(product.selectedVariation?.quantity ?? 0);

      if (existingIndex !== -1) {
        const updated = [...previousCart];

        updated[existingIndex] = {
          ...updated[existingIndex],

          qty: Math.min(quantity, stock),
        };

        return updated;
      }

      return [
        ...previousCart,

        {
          ...product,

          qty: Math.min(quantity, stock),
        },
      ];
    });

    return true;
  };

  /*
    |--------------------------------------------------------------------------
    | REMOVE FROM CART
    |--------------------------------------------------------------------------
    */

  const removeFromCart = (productId, variationId = null, giftId = null) => {
    const itemKey = getCartItemKey(productId, variationId, giftId);

    setCart((previousCart) =>
      previousCart.filter(
        (item) =>
          getCartItemKey(
            item.id,
            item.selectedVariation?.variationId ?? null,
            item.selectedGift?.id ?? null,
          ) !== itemKey,
      ),
    );

    setInvalidCartItems((previous) => {
      const updated = {
        ...previous,
      };

      delete updated[itemKey];

      return updated;
    });
  };

  /*
    |--------------------------------------------------------------------------
    | UPDATE QUANTITY
    |--------------------------------------------------------------------------
    */

  const updateQty = (productId, variationId, qty) => {
    const itemKey = getCartItemKey(productId, variationId, null);

    setCart((previousCart) =>
      previousCart.map((item) => {
        const currentKey = getCartItemKey(
          item.id,
          item.selectedVariation?.variationId ?? null,
          item.selectedGift?.id ?? null,
        );

        if (currentKey !== itemKey) {
          return item;
        }

        const stock = Number(item.selectedVariation?.quantity ?? 0);

        let newQty = Number(qty);

        if (newQty < 1) {
          newQty = 1;
        }

        if (stock > 0 && newQty > stock) {
          newQty = stock;
        }

        return {
          ...item,

          qty: newQty,
        };
      }),
    );

    /*
        |--------------------------------------------------------------------------
        | CLEAR OLD VALIDATION ERROR
        |--------------------------------------------------------------------------
        */

    setInvalidCartItems((previous) => {
      const updated = {
        ...previous,
      };

      delete updated[itemKey];

      return updated;
    });
  };

  /*
    |--------------------------------------------------------------------------
    | GET CART ITEM
    |--------------------------------------------------------------------------
    */

  const getCartItem = (productId, variationId) => {
    return cart.find(
      (item) =>
        Number(item.id) === Number(productId) &&
        Number(item.selectedVariation?.variationId) === Number(variationId),
    );
  };

  /*
    |--------------------------------------------------------------------------
    | INCREASE QUANTITY
    |--------------------------------------------------------------------------
    */

  const increaseQty = async (productId, variationId) => {
    const item = getCartItem(productId, variationId);

    if (!item) {
      return;
    }

    const newQuantity = Number(item.qty) + 1;

    const validationKey = getCartItemKey(
      productId,
      variationId,
      item.selectedGift?.id ?? null,
    );

    try {
      setValidatingItem(validationKey);

      const purchaseData = await validatePurchase({
        productId,

        variationId,

        quantity: newQuantity,

        giftId: item.selectedGift?.id ?? null,
      });

      setCart((previousCart) =>
        previousCart.map((cartItem) => {
          if (
            cartItem.id !== productId ||
            cartItem.selectedVariation?.variationId !== variationId
          ) {
            return cartItem;
          }

          return {
            ...cartItem,

            qty: newQuantity,

            selectedVariation: {
              ...cartItem.selectedVariation,

              quantity: purchaseData.inventory.stock,

              inStock: purchaseData.inventory.inStock,

              isExpired: purchaseData.inventory.expired,

              price: purchaseData.price.original,

              salePrice: purchaseData.price.sale,

              finalPrice: purchaseData.price.final,
            },

            promotion: purchaseData.promotion,

            gifts: purchaseData.gifts,
          };
        }),
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "موجودی محصول کافی نیست.");
    } finally {
      setValidatingItem(null);
    }
  };

  /*
    |--------------------------------------------------------------------------
    | DECREASE QUANTITY
    |--------------------------------------------------------------------------
    */

  const decreaseQty = (productId, variationId) => {
    const item = getCartItem(productId, variationId);

    if (!item) {
      return;
    }

    if (item.qty === 1) {
      removeFromCart(productId, variationId, item.selectedGift?.id ?? null);

      return;
    }

    updateQty(productId, variationId, item.qty - 1);
  };

  /*
    |--------------------------------------------------------------------------
    | IS VALIDATING
    |--------------------------------------------------------------------------
    */

  const isValidating = (productId, variationId) => {
    return validatingItem === getCartItemKey(productId, variationId);
  };

  /*
    |--------------------------------------------------------------------------
    | MOVE TO NEXT CART
    |--------------------------------------------------------------------------
    */

  const moveToNextCart = (productId, variationId = null, giftId = null) => {
    const itemKey = getCartItemKey(productId, variationId, giftId);

    const item = cart.find(
      (cartItem) =>
        getCartItemKey(
          cartItem.id,
          cartItem.selectedVariation?.variationId ?? null,
          cartItem.selectedGift?.id ?? null,
        ) === itemKey,
    );

    if (!item) {
      return;
    }

    setNextCart((previousNextCart) => {
      const alreadyExists = previousNextCart.some(
        (nextItem) =>
          getCartItemKey(
            nextItem.id,
            nextItem.selectedVariation?.variationId ?? null,
            nextItem.selectedGift?.id ?? null,
          ) === itemKey,
      );

      if (alreadyExists) {
        return previousNextCart;
      }

      return [...previousNextCart, item];
    });

    setCart((previousCart) =>
      previousCart.filter(
        (cartItem) =>
          getCartItemKey(
            cartItem.id,
            cartItem.selectedVariation?.variationId ?? null,
            cartItem.selectedGift?.id ?? null,
          ) !== itemKey,
      ),
    );
  };

  /*
    |--------------------------------------------------------------------------
    | REMOVE FROM NEXT CART
    |--------------------------------------------------------------------------
    */

  const removeFromNextCart = (productId, variationId = null, giftId = null) => {
    const itemKey = getCartItemKey(productId, variationId, giftId);

    setNextCart((previousNextCart) =>
      previousNextCart.filter(
        (item) =>
          getCartItemKey(
            item.id,
            item.selectedVariation?.variationId ?? null,
            item.selectedGift?.id ?? null,
          ) !== itemKey,
      ),
    );
  };

  /*
    |--------------------------------------------------------------------------
    | IS MOVING TO CART
    |--------------------------------------------------------------------------
    */

  const isMovingToCart = (productId, variationId = null, giftId = null) => {
    return movingToCartKey === getCartItemKey(productId, variationId, giftId);
  };

  /*
    |--------------------------------------------------------------------------
    | MOVE NEXT CART ITEM TO CART
    |--------------------------------------------------------------------------
    */

  const moveToCart = async (productId, variationId = null, giftId = null) => {
    const itemKey = getCartItemKey(productId, variationId, giftId);

    const item = nextCart.find(
      (nextItem) =>
        getCartItemKey(
          nextItem.id,
          nextItem.selectedVariation?.variationId ?? null,
          nextItem.selectedGift?.id ?? null,
        ) === itemKey,
    );

    if (!item) {
      return;
    }

    try {
      setMovingToCartKey(itemKey);

      const purchaseData = await validatePurchase({
        productId,

        variationId,

        quantity: item.qty || 1,

        giftId,
      });

      const updatedItem = {
        ...item,

        selectedVariation: item.selectedVariation
          ? {
              ...item.selectedVariation,

              quantity: purchaseData.inventory.stock,

              inStock: purchaseData.inventory.inStock,

              isExpired: purchaseData.inventory.expired,

              price: purchaseData.price.original,

              salePrice: purchaseData.price.sale,

              finalPrice: purchaseData.price.final,
            }
          : null,

        promotion: purchaseData.promotion,

        gifts: purchaseData.gifts,
      };

      setCart((previousCart) => {
        const alreadyExists = previousCart.some(
          (cartItem) =>
            getCartItemKey(
              cartItem.id,
              cartItem.selectedVariation?.variationId ?? null,
              cartItem.selectedGift?.id ?? null,
            ) === itemKey,
        );

        if (alreadyExists) {
          return previousCart;
        }

        return [...previousCart, updatedItem];
      });

      setNextCart((previousNextCart) =>
        previousNextCart.filter(
          (nextItem) =>
            getCartItemKey(
              nextItem.id,
              nextItem.selectedVariation?.variationId ?? null,
              nextItem.selectedGift?.id ?? null,
            ) !== itemKey,
        ),
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "این محصول در حال حاضر قابل خرید نیست.",
      );
    } finally {
      setMovingToCartKey(null);
    }
  };

  /*
    |--------------------------------------------------------------------------
    | VALIDATE ENTIRE CART BEFORE CHECKOUT
    |--------------------------------------------------------------------------
    */

  const validateCart = async () => {
    const errors = {};

    const validationResults = await Promise.all(
      cart.map(async (item) => {
        const variationId = item.selectedVariation?.variationId ?? null;

        const giftId = item.selectedGift?.id ?? null;

        const itemKey = getCartItemKey(item.id, variationId, giftId);

        try {
          const purchaseData = await validatePurchase({
            productId: item.id,

            variationId,

            quantity: item.qty,

            giftId,
          });

          /*
                                |--------------------------------------------------------------------------
                                | UPDATE LATEST SERVER DATA
                                |--------------------------------------------------------------------------
                                */

          setCart((previousCart) =>
            previousCart.map((cartItem) => {
              const currentKey = getCartItemKey(
                cartItem.id,
                cartItem.selectedVariation?.variationId ?? null,
                cartItem.selectedGift?.id ?? null,
              );

              if (currentKey !== itemKey) {
                return cartItem;
              }

              return {
                ...cartItem,

                selectedVariation: cartItem.selectedVariation
                  ? {
                      ...cartItem.selectedVariation,

                      quantity: purchaseData.inventory.stock,

                      inStock: purchaseData.inventory.inStock,

                      isExpired: purchaseData.inventory.expired,

                      price: purchaseData.price.original,

                      salePrice: purchaseData.price.sale,

                      finalPrice: purchaseData.price.final,
                    }
                  : null,

                promotion: purchaseData.promotion,

                gifts: purchaseData.gifts,
              };
            }),
          );

          return {
            itemKey,

            valid: true,
          };
        } catch (error) {
          errors[itemKey] =
            error.response?.data?.message || "موجودی این محصول تغییر کرده است.";

          return {
            itemKey,

            valid: false,
          };
        }
      }),
    );

    setInvalidCartItems(errors);

    return validationResults.every((result) => result.valid);
  };

  /*
    |--------------------------------------------------------------------------
    | CLEAR CART
    |--------------------------------------------------------------------------
    */

  const clearCart = () => {
    setCart([]);

    setInvalidCartItems({});
  };

  /*
    |--------------------------------------------------------------------------
    | TOTALS
    |--------------------------------------------------------------------------
    */

  const totalPrice = getTotalPrice(cart);

  const finalPrice = getFinalPrice(cart);

  const discountAmount = getDiscountAmount(cart);

  /*
    |--------------------------------------------------------------------------
    | PROVIDER
    |--------------------------------------------------------------------------
    */

  return (
    <CartContext.Provider
      value={{
        cart,

        nextCart,

        addToCart,

        removeFromCart,

        updateQty,

        increaseQty,

        decreaseQty,

        getCartItem,

        isValidating,

        clearCart,

        moveToNextCart,

        moveToCart,

        isMovingToCart,

        removeFromNextCart,

        validateCart,

        invalidCartItems,

        getCartItemKey,

        totalPrice,

        finalPrice,

        discountAmount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);

export function calculateCartSummary(
  cart,
  shippingPrice = 0,
  couponDiscount = 0,
) {
    
  const items = cart ?? [];

  const itemCount = items.reduce((sum, item) => sum + Number(item.qty || 0), 0);

  const subtotal = items.reduce((sum, item) => {
    const unitPrice = Number(
      item.selectedVariation?.price ?? item.price ?? item.finalPrice ?? 0,
    );

    return sum + unitPrice * item.qty;
  }, 0);

  const productDiscount = items.reduce((sum, item) => {
    const original = Number(item.selectedVariation?.price ?? item.price ?? 0);

    const sale = Number(
      item.selectedVariation?.finalPrice ??
        item.selectedVariation?.salePrice ??
        item.finalPrice ??
        item.lowestSalePrice ??
        original,
    );

    return sum + (original - sale) * item.qty;
  }, 0);

  const payable = subtotal - productDiscount - couponDiscount + shippingPrice;

  return {
    itemCount,
    subtotal,
    productDiscount,
    couponDiscount,
    shippingPrice,
    payable,
  };
}
